import type { LcComponentStatus } from '@/api/lcLearning'

export interface ReviewStep {
  id: string // configComponentId
  type: string
  label: string | null
  sortOrder: number
  status: LcComponentStatus['status']
  component: LcComponentStatus
  blockQuizComponentId?: string
}

/**
 * 도서관에서 현재 노출하는 video / quiz 중 contentScope === "current"인 컴포넌트만 포함.
 * 다른 컴포넌트 데이터와 실행 코드는 유지하고 목록과 직접 진입에서만 숨긴다.
 */
export function filterReviewableComponents(
  components: LcComponentStatus[],
): LcComponentStatus[] {
  return components.filter((c) => isReviewable(c))
}

export function isReviewable(c: LcComponentStatus): boolean {
  const type = c.type.toLowerCase()
  if (type !== 'video' && type !== 'quiz') return false

  const opts = c.options ?? null
  if (!opts) return true
  switch (type) {
    case 'video': {
      const scope = readScope((opts as Record<string, unknown>).source)
      return isCurrentOnly(scope)
    }
    case 'quiz': {
      const extraction = (opts as Record<string, unknown>).extraction
      if (readAnswerFilter(extraction) === 'wrong_in_review') return false
      const scope = readScope(extraction)
      return isCurrentOnly(scope)
    }
  }
}

function readAnswerFilter(container: unknown): unknown {
  if (!container || typeof container !== 'object') return null
  return (container as Record<string, unknown>).answerFilter ?? null
}

function readScope(container: unknown): unknown {
  if (!container || typeof container !== 'object') return null
  return (container as Record<string, unknown>).contentScope ?? null
}

function isCurrentOnly(scope: unknown): boolean {
  if (scope == null) return true
  if (typeof scope === 'string') return scope === 'current'
  if (Array.isArray(scope)) {
    if (scope.length === 0) return true
    return scope.every((v) => v === 'current')
  }
  return true
}

export function toReviewSteps(components: LcComponentStatus[]): ReviewStep[] {
  const reviewable = filterReviewableComponents(components)
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
  const steps: ReviewStep[] = []
  let mainVideoStep: ReviewStep | undefined

  for (const c of reviewable) {
    const step: ReviewStep = {
      id: c.configComponentId,
      type: c.type,
      label: c.label,
      sortOrder: c.sortOrder,
      status: c.status,
      component: c,
    }

    if (isMainVideo(c)) {
      if (!mainVideoStep) {
        if (hasAvailableBlockQuiz(c)) {
          step.blockQuizComponentId = c.configComponentId
        }
        mainVideoStep = step
        steps.push(step)
      } else if (!mainVideoStep.blockQuizComponentId && hasAvailableBlockQuiz(c)) {
        mainVideoStep.blockQuizComponentId = c.configComponentId
      }
      continue
    }

    steps.push(step)
  }

  return steps
}

function isMainVideo(component: LcComponentStatus): boolean {
  if (component.type.toLowerCase() !== 'video') return false
  const source = (component.options as Record<string, unknown> | null)?.source
  if (!source || typeof source !== 'object') return true
  const subtype = (source as Record<string, unknown>).subtype
  return subtype == null || subtype === 'main'
}

function hasAvailableBlockQuiz(component: LcComponentStatus): boolean {
  const pauseEnabled = (
    component.options as { pauseEnabled?: boolean } | null
  )?.pauseEnabled === true
  return pauseEnabled && component.hasPauseQuizResponse === true
}
